// Demo scenario ?scenario=type_change — zone identity phase 6.2 (demo first).
//
// "Garden Utilities" was a Product Zone and was changed to a Display Zone TODAY. Until
// the nightly run its figures are still product figures, so the screens take it out of
// the product type (charts, Top / Lowest cards — the next zone in line — and hot / cold)
// and do not show it under Display yet; its own page says why. Production gets the same
// behaviour from the register (GET /api_db/changes_today/) and the backend.

import { TYPE_TOKENS } from '@/zones/changesToday'
import { demoDateStr } from '@/api/demoData'

// The demo's type-change scenarios (zone, old type, new type, the zone's demo id).
// ?scenario=type_change_entrance (6.4): Main Entrance became a Combined Entrance/Exit
// Zone — a change to/from an entrance type moves store footfall itself.
export const SCENARIO_CHANGES = {
  type_change: { zone: 'Garden Utilities', from: 6, to: 8, key: 91 },
  type_change_entrance: { zone: 'Main Entrance', from: 1, to: 3, key: 87 },
}
export const TYPE_CHANGE = SCENARIO_CHANGES.type_change

const registerOf = (c) => ({
  zones: { [c.key]: { from: c.from, to: c.to, name: c.zone, at: new Date().toISOString() } },
  settings: {},
})

// What GET /api_db/changes_today/ would answer in the product scenario.
export const TYPE_CHANGE_REGISTER = registerOf(TYPE_CHANGE)

// Settings saved today (6.7): ?scenario=money_change | opening_hours_change |
// threshold_change (the Customer Service Desk's alert threshold, demo zone 90).
export const SCENARIO_SETTINGS = {
  money_change: { money: { zones: [] } },
  opening_hours_change: { opening_hours: { zones: [] } },
  threshold_change: { thresholds: { zones: [90] } },
}
export const THRESHOLD_ZONE = { zone: 'Customer Service Desk', key: 90, typeId: 10 }

// The scenario's TYPE change for this route query, or null.
export const changeFor = (query) => (query && SCENARIO_CHANGES[query.scenario]) || null
// Its register ({zones:{}, settings:{}} when no scenario).
export const registerFor = (query) => {
  const c = changeFor(query)
  if (c) return registerOf(c)
  const st = query && SCENARIO_SETTINGS[query.scenario]
  if (!st) return { zones: {}, settings: {} }
  const at = new Date().toISOString()
  return { zones: {}, settings: Object.fromEntries(Object.entries(st).map(([k, v]) => [k, { ...v, at }])) }
}
export const isTypeChangeScenario = (query) => !!changeFor(query)

// The widget without `zone` in the fields of type `typeId`; Top / Lowest recomputed
// from what is left. A new object; the input is not touched.
export function withoutZoneInType(fw, zone, typeId) {
  const tok = TYPE_TOKENS[typeId]
  if (!fw || !tok) return fw
  const field = new RegExp(`(^|_)${tok}_(zone|zones|hotcold)(_|$)`)
  const out = { ...fw }
  for (const [k, v] of Object.entries(fw)) {
    if (!field.test(k) || !v || typeof v !== 'object') continue
    if (Array.isArray(v)) {
      const kept = v.filter((x) => !(x && x.zone === zone))
      const total = kept.reduce((s, x) => s + (Number(x && x.share) || 0), 0)
      out[k] = total > 0 && kept.every((x) => x && typeof x.share === 'number')
        ? kept.map((x) => ({ ...x, share: x.share / total }))
        : kept
    } else if (Object.prototype.hasOwnProperty.call(v, zone)) {
      const { [zone]: _drop, ...rest } = v
      out[k] = rest
    }
  }
  const visitors = out[`${tok}_zone_visitors_counts`] || {}
  const durations = out[`total_duration_per_${tok}_zone`] || {}
  const names = Object.keys(visitors).filter((z) => Number(visitors[z]) > 0)
  const byVisitors = [...names].sort((a, b) => visitors[b] - visitors[a])
  const best = (k, value) => {
    if (fw[k] && fw[k].zone_name === zone) out[k] = value
  }
  best(`best_${tok}_zone_by_visitors`, byVisitors.length
    ? { zone_name: byVisitors[0], visitor_count: visitors[byVisitors[0]] } : null)
  best(`worst_${tok}_zone_by_visitors`, byVisitors.length
    ? { zone_name: byVisitors[byVisitors.length - 1], visitor_count: visitors[byVisitors[byVisitors.length - 1]] } : null)
  const avg = names.map((z) => [z, Number(durations[z] || 0) / Number(visitors[z])])
    .sort((a, b) => b[1] - a[1])
  best(`best_${tok}_zone_by_avg_duration`, avg.length
    ? { zone_name: avg[0][0], avg_duration: Math.round(avg[0][1] * 100) / 100 } : null)
  return out
}

// The zone list after the save: the zone already carries its NEW type.
export function withNewType(zones, zone, typeId) {
  return (zones || []).map((z) => (z && z.DetectionZoneName === zone
    ? { ...z, DetectionZoneTypeId: typeId } : z))
}

export function applyChange(data, c) {
  return {
    ...data,
    fixedWidgetsData: withoutZoneInType(data.fixedWidgetsData, c.zone, c.from),
    compareData: withoutZoneInType(data.compareData, c.zone, c.from),
    zones: withNewType(data.zones, c.zone, c.to),
    // 6.3: Home reads the register (goal notice, findings about the zone hidden). The
    // demo goals carry no zone field: link the one that names it.
    changesToday: registerOf(c),
    goals: (data.goals || []).map((g) => (g && !g.zone && String(g.title || '').includes(c.zone)
      ? { ...g, zone: c.zone } : g)),
  }
}
export const applyTypeChange = (data) => applyChange(data, TYPE_CHANGE)
export const applyEntranceTypeChange = (data) => applyChange(data, SCENARIO_CHANGES.type_change_entrance)

// Home for the settings scenarios: only the register changes, the figures do not.
export const applySettingsChange = (scenario) => (data) => ({
  ...data, changesToday: registerFor({ scenario }),
})

// ?scenario=zone_marker — settings step 2c (demo first): ten days ago the shop answered
// "Something changed at this spot" when Garden Tools became Garden Utilities. What
// GET /api_db/zone_markers/?zoneKey= would answer for that zone; [] for any other zone.
export const MARKER_SCENARIO = { zone: 'Garden Utilities', label: 'Garden Tools → Garden Utilities', daysAgo: 10 }
export const demoMarkersFor = (query, zoneName) =>
  query && query.scenario === 'zone_marker' && zoneName === MARKER_SCENARIO.zone
    ? [{ MarkerDate: demoDateStr(MARKER_SCENARIO.daysAgo), Label: MARKER_SCENARIO.label }]
    : []
